/**
 * @file emailChange.ts
 * @brief Changing the account email (B34, ADR-059): the pure parts of the
 *        web client. The Profile "Change email" step and the public undo
 *        page `/auth/email-undo` use them. No app imports, so `node --test`
 *        runs it as is.
 *
 * @details
 * - Profile: `PUT /api/auth/profile` with only `{ email, currentPassword? }`
 *   (+ `twoFactorCode` / `backupCode` after 403 REAUTH_REQUIRED with
 *   `twoFactorAllowed`). Only the email goes in that save, so a refused
 *   re-auth never loses the name / phone edits of the big form, and saving
 *   those never asks for re-auth.
 * - Undo page: plain fetch, not the app's API client (no session token, no
 *   device id). GET only reads (mail scanners open links); the undo is a
 *   POST from a button. The token is never logged or stored.
 *
 * @references
 * - docs/product/email-change-reauth.md (contract)
 * - BACKEND/src/features/auth/emailChange.service.mjs, routes.js
 *
 * @author DES (prepared with Claude)
 * @date 26-10-2026
 */

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

/** A different mailbox. Case and spaces alone are not a change (server rule). */
export function isEmailChange(current: string, next: string): boolean {
  return next.trim().toLowerCase() !== current.trim().toLowerCase();
}

export function isLikelyEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** 403 REAUTH_REQUIRED for a password account: the password box is the proof. */
export function needsPassword(code: string | undefined, body: unknown): boolean {
  return code === "REAUTH_REQUIRED" && obj(body)?.passwordRequired === true;
}

/** The line under the Change email form for an answer the re-auth step does not show. */
export function emailChangeErrorText(code: string | undefined, message: string | undefined): string {
  switch (code) {
    case "EMAIL_EXISTS":
      return "Another account uses that email. Type a different one.";
    case "INVALID_PASSWORD":
      return "Current password is incorrect.";
    case "EMAIL_NOTICE_FAILED":
      return "We couldn't send the notice to your old address, so nothing changed. Try again.";
    case "OWNER_PROTECTED":
      return message || "The Owner account's email can't be changed here.";
    case "RATE_LIMITED":
      return message || "Too many attempts. Please wait a few minutes and try again.";
    default:
      return message || "We couldn't change your email. Please try again.";
  }
}

// ----------------------------------------------------------------------
// Undo page: GET (reads) and POST (undoes) /api/auth/email-change/undo
// ----------------------------------------------------------------------

export type EmailUndoResult =
  | { ok: true; code: string; message: string; data: Record<string, unknown> }
  | { ok: false; status: number; code: string; message: string };

export type EmailUndoInfo = {
  restoresEmail: string;
  changedToMasked: string;
  changedAt: string | null;
  expiresAt: string | null;
  whatHappens: string;
};

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const FALLBACK: Record<string, string> = {
  NETWORK: "We couldn't reach VERBLIZR. Check your connection and try again.",
  LINK_INVALID:
    "This link has expired, has already been used, or a later undo replaced it. If you still can't sign in, contact support.",
  EMAIL_TAKEN: "Another account now uses this email, so we can't put it back. Please contact support.",
  RATE_LIMITED: "Too many attempts. Please wait a while and try again.",
};

export function createEmailUndoApi(baseUrl: string, fetchImpl: FetchLike = (u, i) => fetch(u, i)) {
  const root = `${baseUrl.replace(/\/$/, "")}/api/auth/email-change/undo`;

  async function call(url: string, init?: RequestInit): Promise<EmailUndoResult> {
    let res: Response;
    try {
      res = await fetchImpl(url, { ...init, cache: "no-store", credentials: "omit", referrerPolicy: "no-referrer" });
    } catch {
      return { ok: false, status: 0, code: "NETWORK", message: FALLBACK.NETWORK };
    }
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      // empty or not JSON
    }
    const code = typeof body.code === "string" ? body.code : res.ok ? "OK" : "INTERNAL_ERROR";
    const message =
      typeof body.message === "string" && body.message
        ? body.message
        : (FALLBACK[code] ?? "We couldn't finish this. Please try again.");
    if (!res.ok || body.success === false) return { ok: false, status: res.status, code, message };
    return { ok: true, code, message, data: body };
  }

  return {
    /** What undoing does. Changes nothing. */
    lookup: (token: string) => call(`${root}?token=${encodeURIComponent(token)}`),
    /** Undo, once. Signs out every session of the account. */
    undo: (token: string) =>
      call(root, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) }),
  };
}

export function parseUndoInfo(data: Record<string, unknown>): EmailUndoInfo | null {
  const restoresEmail = typeof data.restoresEmail === "string" ? data.restoresEmail : null;
  if (!restoresEmail) return null;
  return {
    restoresEmail,
    changedToMasked: typeof data.changedToMasked === "string" ? data.changedToMasked : "",
    changedAt: typeof data.changedAt === "string" ? data.changedAt : null,
    expiresAt: typeof data.expiresAt === "string" ? data.expiresAt : null,
    whatHappens:
      typeof data.whatHappens === "string" && data.whatHappens
        ? data.whatHappens
        : "Undo puts this email back on your account, signs out every device and forgets trusted devices. Then reset your password.",
  };
}
